/**
 * VesselFinder's vessel popup markup — pure string builders, no Leaflet, no
 * Angular, no component state.
 *
 * SAME CARD as Voyage Radar's (`ais/voyage-radar/vessel-popup.ts`) — same
 * `ais-popup`/`ais-popup__*` classes, styled globally in `client/src/styles.scss`
 * (Leaflet mounts popups outside the component's DOM, so scoped styles never
 * reach them; `AisstreamPlaybackController` already reuses this exact card for
 * a third provider, so this is the established pattern, not a new one).
 *
 * Renders the FLAT normalized shape `eetaskapi/operatorApi/vesselFinderNormalize.js`
 * produces.
 */

import { VesselFinderPopupActions } from './vessel-popup.types';
import { esc } from '../shared/models/html-escape';

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Mon, 27 Jul 2026 17:24:08 UTC" — same grammar as Voyage Radar's card. */
function formatPopupTime(raw: any): string {
  if (!raw) return '—';
  const d = new Date(raw);
  if (isNaN(d.getTime())) return '—';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${DAYS[d.getUTCDay()]}, ${pad(d.getUTCDate())} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()} ` +
    `${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())} UTC`
  );
}

/** One key/value line, or nothing when there is no value — `511`/`360`/`102.3` sentinels are already `null` by the time this runs (backend normalizer). */
function row(label: string, val: any, suffix = ''): string {
  if (val == null || val === '') return '';
  return (
    `<div class="ais-popup__row"><span class="ais-popup__k">${label}:</span>` +
    `<span class="ais-popup__v">${esc(val)}${suffix}</span></div>`
  );
}

/** "Lat: 1.2646, Lng: 103.8201" — same grammar/precision as Voyage Radar's card, never a printed NaN. */
function positionRow(lat: any, lng: any): string {
  if (lat == null || lng == null) return '';
  const la = Number(lat);
  const ln = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(ln)) return '';
  return (
    `<div class="ais-popup__row"><span class="ais-popup__k">Position:</span>` +
    `<span class="ais-popup__v ais-popup__pos">Lat: ${la.toFixed(4)}, Lng: ${ln.toFixed(4)}</span></div>`
  );
}

/**
 * A bought dataset's own titled block, rendered from the moment the action
 * lands — `data` present but empty still renders, saying so, because "nothing
 * appeared" is indistinguishable from "the click did nothing" otherwise.
 */
function dataSection(title: string, data: any, fields: Array<[string, any, string?]>): string {
  if (data == null) return '';
  const rows = fields.map(([label, value, suffix]) => row(label, value, suffix)).join('');
  return (
    `<div class="ais-popup__section"><div class="ais-popup__section-title">${title}</div>` +
    (rows || `<div class="ais-popup__empty">Not reported for this vessel.</div>`) +
    `</div>`
  );
}

/**
 * The complete VesselFinder vessel popup — same shell as Voyage Radar's
 * `buildVesselPopup`: a coloured dot + name header with icon actions, then
 * key/value rows.
 *
 * Icons: Watch/un-watch (always present, free), then Master, Voyage, Port
 * calls (all priced — amber, the same family Voyage Radar's own paid popup
 * icons use), then Close (always last).
 */
export function buildVesselFinderPopup(vessel: any, actions: VesselFinderPopupActions = {}): string {
  const v = vessel || {};
  const name = esc(v.name || (v.mmsi ? `MMSI ${v.mmsi}` : v.imo ? `IMO ${v.imo}` : 'Unknown'));
  const key = String(v.mmsi || v.imo || '').trim();

  const acts: string[] = [];
  if (key) {
    const watchLabel = actions.kept ? 'Remove from Watched Vessels' : 'Watch vessel';
    acts.push(
      `<button type="button" class="ais-popup__act ais-popup__save" ` +
      `data-card-action="${actions.kept ? 'remove' : 'save'}" data-key="${esc(key)}" ` +
      `title="${watchLabel}" aria-label="${watchLabel}">` +
      `<span class="material-icons-outlined">${actions.kept ? 'bookmark' : 'bookmark_add'}</span></button>`,
    );
  }
  if (key && actions.masterCredits != null) {
    const c = actions.masterCredits;
    const label = `Master data · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    acts.push(
      `<button type="button" class="ais-popup__act ais-popup__master" ` +
      `data-card-action="master" data-key="${esc(key)}" title="${label}" aria-label="${label}">` +
      `<span class="material-icons-outlined">description</span></button>`,
    );
  }
  if (key && actions.voyageCredits != null) {
    const c = actions.voyageCredits;
    const label = `Voyage data · ${esc(c)} ${c === 1 ? 'credit' : 'credits'}`;
    acts.push(
      `<button type="button" class="ais-popup__act ais-popup__voyage" ` +
      `data-card-action="voyage" data-key="${esc(key)}" title="${label}" aria-label="${label}">` +
      `<span class="material-icons-outlined">route</span></button>`,
    );
  }
  acts.push(
    `<button type="button" class="ais-popup__act ais-popup__close" title="Close" aria-label="Close">` +
    `<span class="material-icons-outlined">close</span></button>`,
  );
  const menu = `<span class="ais-popup__acts">${acts.join('')}</span>`;

  // Directly under the header, NOT at the foot of the card: `.ais-popup--vf` is
  // a `max-height`/`overflow-y: auto` scroll box, so a note appended after the
  // Master/Voyage/Port-call sections sits below the fold on any hull that has
  // them — "No master data returned" went unread because of it.
  const note = actions.note ? `<div class="ais-popup__note">${esc(actions.note)}</div>` : '';

  const body =
    `<div class="ais-popup__head"><span class="ais-popup__dot" style="background:#B45309"></span>` +
      `<span class="ais-popup__name">${name}</span>${menu}</div>` +
    note +
    `<div class="ais-popup__row"><span class="ais-popup__k">MMSI:</span><span class="ais-popup__v ais-popup__mmsi">${esc(v.mmsi || '—')}</span></div>` +
    positionRow(v.latitude, v.longitude) +
    row('IMO', v.imo) +
    row('Call sign', v.callSign) +
    row('Type', v.shipType ?? v.typeCode) +
    row('Status', v.navStatus) +
    (v.speed != null ? `<div class="ais-popup__row"><span class="ais-popup__k">SOG:</span><span class="ais-popup__v">${esc(v.speed)} kn</span></div>` : '') +
    (v.course != null ? `<div class="ais-popup__row"><span class="ais-popup__k">COG:</span><span class="ais-popup__v">${esc(Math.round(Number(v.course)))}°</span></div>` : '') +
    row('Heading', v.heading, '°') +
    row('Flag', v.flag) +
    row('Destination', v.destination) +
    row('ETA', v.eta) +
    row('Position source', v.positionSource === 'SAT' ? 'Satellite' : v.positionSource === 'TER' ? 'Terrestrial' : null) +
    row('Last port', v.lastPort) +
    row('Departed', v.departure) +
    `<div class="ais-popup__row"><span class="ais-popup__k">Time:</span><span class="ais-popup__v ais-popup__time">${formatPopupTime(v.timestamp)}</span></div>` +
    dataSection('Master data', v.master, [
      ['Flag', v.master?.flag],
      ['Built', v.master?.built],
      ['Builder', v.master?.builder],
      ['Owner', v.master?.owner],
      ['Manager', v.master?.manager],
      ['Class', v.master?.class],
      ['Beam', v.master?.beam, ' m'],
      ['Max draught', v.master?.maxDraught, ' m'],
      ['Gross tonnage', v.master?.gt],
      ['Net tonnage', v.master?.nt],
      ['Deadweight', v.master?.dwt],
      ['TEU', v.master?.teu],
    ]) +
    dataSection('Voyage data', v.voyage, [
      ['Locode', v.voyage?.lastPortLocode],
      ['Last port', v.voyage?.lastPort],
      ['Last country', v.voyage?.lastCountry],
      ['Departure', v.voyage?.departure],
    ]);

  const stamp = key ? ` data-key="${esc(key)}"` : '';
  return `<div class="ais-popup ais-popup--vf"${stamp}>${body}</div>`;
}
